import { useState } from 'react'
import { motion as Motion, useReducedMotion } from 'motion/react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { useAuth } from './hooks/useAuth'
import { Sidebar } from './components/Sidebar'
import { Header } from './components/Header'
import { PWAUpdateBanner } from './components/Common/PWAUpdateBanner'
import { Login } from './pages/Login'
import { Signup } from './pages/Signup'
import { ResetPassword } from './pages/ResetPassword'
import { LoadingSpinner } from './components/Common/LoadingSpinner'
import { Dashboard } from './components/Dashboard/Dashboard'
import { CustomersList } from './components/Customers/CustomersList'
import { PoliciesList } from './components/Policies/PoliciesList'
import { CommissionsList } from './components/Commissions/CommissionsList'
import { PolicyCommissionReport } from './pages/Commissions/PolicyCommissionReport'
import { ProductProvidersList } from './components/ProductProviders/ProductProvidersList'
import { ClientsList } from './pages/Clients/ClientsList'
import { EntitiesList } from './pages/Entities/EntitiesList'
import { RolesList } from './pages/Admin/RolesList'
import { UsersList } from './pages/Admin/UsersList'
import { TaskSectionsAdmin } from './pages/Admin/TaskSectionsAdmin'
import { SettingsPage } from './pages/SettingsPage'
import { BoardroomAI_Option2 } from './pages/Boardroom/BoardroomAI_Option2_DarkModern'
import { TasksPage } from './pages/Tasks/TasksPage'
import { ClaimsTasksPage } from './pages/Tasks/ClaimsTasksPage'
import { useTaskNotificationCounts } from './hooks/useTaskNotificationCounts'
import { InboxPage } from './pages/Inbox/InboxPage'
import { useInboxCounts } from './hooks/useInboxCounts'
import './styles/tailwind.css'

function App() {
  const [currentPage, setCurrentPage] = useState('clients')
  const [pageParams, setPageParams] = useState({})
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('sfp_dark_mode') === 'true'
  })
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW()
  const { user, loading, canView } = useAuth()
  const { claimsCount: claimsUnreadCount, tasksCount: tasksUnreadCount } = useTaskNotificationCounts()
  const inboxUnreadCount = useInboxCounts()
  const shouldReduceMotion = useReducedMotion()

  const toggleDarkMode = () => {
    const newMode = !isDarkMode
    setIsDarkMode(newMode)
    localStorage.setItem('sfp_dark_mode', newMode ? 'true' : 'false')
  }

  if (loading) {
    return <LoadingSpinner />
  }

  // Show Signup page if on /auth/signup path
  if (window.location.pathname === '/auth/signup') {
    return <Signup />
  }

  // Show Reset Password page if on /auth/reset-password path
  if (window.location.pathname === '/auth/reset-password') {
    return <ResetPassword />
  }

  if (!user) {
    return <Login />
  }

  const getPageConfig = () => {
    const configs = {
      dashboard: {
        component: <Dashboard isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home', current: true }],
        title: 'Dashboard'
      },
      customers: {
        component: <CustomersList />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Customers', current: true }],
        title: 'Customers'
      },
      policies: {
        component: <PoliciesList sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Policies' }, { label: 'Policies', current: true }],
        title: 'Policies'
      },
      commissions: {
        component: <CommissionsList sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Policies' }, { label: 'Policy Transactions', current: true }],
        title: 'Policy Transactions'
      },
      policy_transactions: {
        component: <CommissionsList sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Policies' }, { label: 'Policy Transactions', current: true }],
        title: 'Policy Transactions'
      },
      commission_report: {
        component: <PolicyCommissionReport sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Policies' }, { label: 'Commission Report', current: true }],
        title: 'Commission Report'
      },
      providers: {
        component: <ProductProvidersList sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Policies' }, { label: 'Product Providers', current: true }],
        title: 'Product Providers'
      },
      entities: {
        component: <EntitiesList sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Client' }, { label: 'Entities', current: true }],
        title: 'Entities'
      },
      clients: {
        component: <ClientsList sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Client' }, { label: 'Natural Persons', current: true }],
        title: 'Natural Persons'
      },
      roles: {
        component: <RolesList sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Admin' }, { label: 'Roles', current: true }],
        title: 'Roles'
      },
      users: {
        component: <UsersList sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Admin' }, { label: 'Users', current: true }],
        title: 'Users'
      },
      task_sections: {
        component: <TaskSectionsAdmin isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Admin' }, { label: 'Task Sections', current: true }],
        title: 'Task Sections'
      },
      settings: {
        component: <SettingsPage isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Settings', current: true }],
        title: 'Settings'
      },
      boardroom_tv: {
        component: <BoardroomAI_Option2 isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Dashboards' }, { label: 'Boardroom TV [proposed dashboards]', current: true }],
        title: 'Boardroom TV'
      },
      dashboards_key_metrics: {
        component: <Dashboard isDarkMode={isDarkMode} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Dashboards' }, { label: 'Key Metrics [proposed dashboards]', current: true }],
        title: 'Key Metrics Dashboard'
      },
      claims_tasks: {
        component: <ClaimsTasksPage isDarkMode={isDarkMode} initialTaskId={pageParams.taskId} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Dispatch Claims', current: true }],
        title: 'Dispatch Claims'
      },
      tasks: {
        component: <TasksPage isDarkMode={isDarkMode} initialTaskId={pageParams.taskId} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Tasks', current: true }],
        title: 'Tasks'
      },
      inbox: {
        component: <InboxPage isDarkMode={isDarkMode} onNavigate={navigate} />,
        breadcrumbs: [{ label: 'Home' }, { label: 'Inbox', current: true }],
        title: 'Inbox'
      },
    }
    return configs[currentPage] || configs.dashboard
  }

  const navigate = (page, params = {}) => {
    setCurrentPage(page)
    setPageParams(params)
    setIsMobileOpen(false)
  }

  const pageConfig = getPageConfig()

  return (
    <div className={`flex flex-col h-dvh transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-sfp-white'
    }`}>
      <Header
        breadcrumbs={pageConfig.breadcrumbs}
        title={pageConfig.title}
        sidebarCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        isDarkMode={isDarkMode}
        onToggleDarkMode={toggleDarkMode}
        isMobileOpen={isMobileOpen}
        onMobileToggle={setIsMobileOpen}
        onNavigate={navigate}
      />
      <div className="flex flex-1 overflow-hidden">
        {/* Mobile overlay backdrop */}
        {isMobileOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-20 lg:hidden"
            onClick={() => setIsMobileOpen(false)}
          />
        )}

        <Sidebar
          currentPage={currentPage}
          onNavigate={(page) => navigate(page)}
          isCollapsed={sidebarCollapsed}
          canView={canView}
          isDarkMode={isDarkMode}
          isMobileOpen={isMobileOpen}
          onMobileClose={() => setIsMobileOpen(false)}
          userEmail={user?.email || ''}
          claimsUnreadCount={claimsUnreadCount}
          tasksUnreadCount={tasksUnreadCount}
          inboxUnreadCount={inboxUnreadCount}
        />
        <div className={`flex-1 overflow-auto transition-colors duration-300 ${
          isDarkMode ? 'bg-slate-900' : 'bg-sfp-lightgray'
        }`}>
          <Motion.div
            key={currentPage}
            className="h-full"
            initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            {pageConfig.component}
          </Motion.div>
        </div>
      </div>
      {needRefresh && (
        <PWAUpdateBanner
          onUpdate={() => updateServiceWorker(true)}
          onDismiss={() => setNeedRefresh(false)}
        />
      )}
    </div>
  )
}

export default App
